import {
    DATIQ_DEFAULT_ARRAY,
    DATIQ_DEFAULT_EMPTY_LABEL,
    DATIQ_DEFAULT_STRING,
    DATIQ_INPUT,
    datiqInputCreateId,
    IDatiqDateRange,
    IDatiqFilter,
    IDatiqGeoSuggestion,
    IDatiqNumberRange,
} from '@dat/datiq-core/models';

/**
 * This is a local interface to make the input values right
 * and stop a circular dependency.
 */
interface IDatiqChecklistItem extends IDatiqOption {
    subOptions?: IDatiqOption[];
    selectedOption?: IDatiqOption;
}

/**
 * This is a local interface to make the input values right
 * and stop a circular dependency.
 */
interface IDatiqTimelineTab {
    value: Date | string;
    label: string;
    selected?: boolean;
    data?: unknown;
    disabled?: boolean;
    tooltip?: string;
}

import { IDatiqOption } from './datiq-components.interface';
import { DatiqInputErrorTypes, DatiqInputTypes } from './datiq-input.enum';
import {
    IDatiqForm,
    IDatiqInput,
    IDatiqInputCurrencyOptions,
    IDatiqInputErrorMessages,
} from './datiq-input.interface';

// TODO: Move to values after migration
const DATIQ_DEFAULT_ID: (prefix?: string) => string = (prefix?: string) =>
    `${!!prefix ? prefix + '-' : ''}${(Math.random() + 1)
        .toString(36)
        .substring(7)}`;

export type DatiqInputValueTypes =
    | number
    | string
    | boolean
    | IDatiqOption
    | IDatiqOption[]
    | IDatiqChecklistItem
    | IDatiqChecklistItem[]
    | IDatiqFilter
    | IDatiqTimelineTab
    | Date
    | IDatiqDateRange
    | IDatiqNumberRange
    | IDatiqGeoSuggestion;

export const DATIQ_INPUT_SYNCABLE_TYPES: DatiqInputTypes[] = [
    DatiqInputTypes.Percent,
];

export const DATIQ_INPUT_ID: (id: string) => string = (id: string): string =>
    datiqInputCreateId([id, DATIQ_INPUT]);
export const DATIQ_INPUT_PERCENT_PERCISION = 4;
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN_LENGTH =
    'Too short. Make sure it is over: {{minlength.requiredLength}} character.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX_LENGTH =
    'Too long. Make sure it is under: {{maxlength.requiredLength}} characters.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN =
    'Too low. Min value is {{min.min}}.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX =
    'Too high. Max value is {{max.max}}.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_PATTERN =
    'The text is invalid. Valid regex is {{pattern.requiredPattern}}';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_REQUIRED = '*Required';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_PERCENT_MIN =
    'Too low. Min value is {{percentmin.minValue}}%.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_PERCENT_MAX =
    'Too high. Max percent is {{percentmax.maxValue}}%.';

export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN_DATE =
    'Too early. Min date is {{datemin.min}}.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX_DATE =
    'Too late. Max date is {{datemax.max}}.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_DATERANGE = 'Invalid range';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_DATEINVALID = 'Invalid date';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN_DAYS =
    'Must be at least {{dateMinDays.min}} days.';
export const DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX_DAYS =
    'Must be at most {{dateMaxDays.max}} days.';
export const DATIQ_DEFAULT_INPUT_ERROR_GEO_INDETERMINATE = `Cannot determine {{${DatiqInputErrorTypes.GeoIndeterminate}.key}} from {{${DatiqInputErrorTypes.GeoIndeterminate}.search}}.`;
export const DATIQ_DEFAULT_INPUT_ERROR_SELECT_MATCHES_OPTION = `Select must match one of the provided options.`;

export const emptyDatiqInputErrorMessages: IDatiqInputErrorMessages = {
    [DatiqInputErrorTypes.MinLength]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN_LENGTH,
    [DatiqInputErrorTypes.MaxLength]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX_LENGTH,
    [DatiqInputErrorTypes.Min]: DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN,
    [DatiqInputErrorTypes.Max]: DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX,
    [DatiqInputErrorTypes.Pattern]: DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_PATTERN,
    [DatiqInputErrorTypes.Required]: DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_REQUIRED,
    [DatiqInputErrorTypes.PercentMin]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_PERCENT_MIN,
    [DatiqInputErrorTypes.PercentMax]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_PERCENT_MAX,
    [DatiqInputErrorTypes.DateMin]: DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN_DATE,
    [DatiqInputErrorTypes.DateMax]: DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX_DATE,
    [DatiqInputErrorTypes.DateRange]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_DATERANGE,
    [DatiqInputErrorTypes.DateInvalid]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_DATEINVALID,
    [DatiqInputErrorTypes.DateMinDays]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MIN_DAYS,
    [DatiqInputErrorTypes.DateMaxDays]:
        DATIQ_DEFAULT_INPUT_ERROR_MESSAGE_MAX_DAYS,
    [DatiqInputErrorTypes.GeoPartial]:
        DATIQ_DEFAULT_INPUT_ERROR_GEO_INDETERMINATE,
    [DatiqInputErrorTypes.SelectMatchesOptions]:
        DATIQ_DEFAULT_INPUT_ERROR_SELECT_MATCHES_OPTION,
};

export const emptyDatiqInputCurrencyOptions: IDatiqInputCurrencyOptions = {
    align: 'left',
};

export const emptyDatiqInput: IDatiqInput = {
    id: DATIQ_DEFAULT_ID('datiq-input'),
    name: DATIQ_DEFAULT_ID('datiq-input'),
    label: 'Input',
    type: DatiqInputTypes.Text,
    errorMessages: { ...emptyDatiqInputErrorMessages },
    currencyOptions: { ...emptyDatiqInputCurrencyOptions },
};

export const emptyDatiqOption: IDatiqOption = {
    display: DATIQ_DEFAULT_EMPTY_LABEL,
    value: DATIQ_DEFAULT_STRING,
};

const DatiqFormSubmitLabelDefault = `Submit`;
const DatiqFormResetLabelDefault = `Reset`;

export const emptyDatiqForm: IDatiqForm = {
    id: DATIQ_INPUT_ID('datiq-form'),
    inputs: DATIQ_DEFAULT_ARRAY as IDatiqInput[],
    showResetButton: true,
    showSubmitButton: true,
    submitLabel: DatiqFormSubmitLabelDefault,
    resetLabel: DatiqFormResetLabelDefault,
};
